import types from '../mutation-types';
import BulkCampaignsAPI from '../../api/bulkCampaigns';

export const state = {
  records: [],
  totalCount: 0,
  currentRecord: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isLoadingRecord: false,
  },
};

export const getters = {
  getAll(_state) {
    return _state.records;
  },
  getTotalCount(_state) {
    return _state.totalCount;
  },
  getCurrent(_state) {
    return _state.currentRecord;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  async getAll(
    { commit },
    { status, inboxId, limit, periodType, date, page = 1 } = {}
  ) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const response = await BulkCampaignsAPI.getAll({
        status,
        inboxId,
        limit,
        periodType,
        date,
        page,
      });
      const total = Number(response.headers?.['x-total-count'] || 0);
      if (page > 1) {
        commit(types.APPEND_BULK_CAMPAIGNS, response.data);
      } else {
        commit(types.SET_BULK_CAMPAIGNS, response.data);
      }
      commit(types.SET_BULK_CAMPAIGNS_TOTAL, total);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isFetching: false,
      });
    }
  },

  async get({ commit }, { id }) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isLoadingRecord: true });
    try {
      const { data } = await BulkCampaignsAPI.get(id);
      commit(types.SET_BULK_CAMPAIGN_CURRENT, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isLoadingRecord: false,
      });
    }
  },

  async create({ commit }, { payload }) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await BulkCampaignsAPI.create(payload);
      commit(types.ADD_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isCreating: false,
      });
    }
  },

  async update({ commit }, { id, payload }) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await BulkCampaignsAPI.update(id, payload);
      commit(types.UPDATE_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isUpdating: false,
      });
    }
  },

  async delete({ commit }, { id }) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isDeleting: true });
    try {
      await BulkCampaignsAPI.delete(id);
      commit(types.DELETE_BULK_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isDeleting: false,
      });
    }
  },

  async cancel({ commit }, { id }) {
    try {
      const { data } = await BulkCampaignsAPI.cancel(id);
      commit(types.UPDATE_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async pause({ commit }, { id }) {
    try {
      const { data } = await BulkCampaignsAPI.pause(id);
      commit(types.UPDATE_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async resume({ commit }, { id }) {
    try {
      const { data } = await BulkCampaignsAPI.resume(id);
      commit(types.UPDATE_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async sendNow({ commit }, { id }) {
    try {
      const { data } = await BulkCampaignsAPI.sendNow(id);
      commit(types.UPDATE_BULK_CAMPAIGN, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  clearCurrent({ commit }) {
    commit(types.CLEAR_BULK_CAMPAIGN_CURRENT);
  },

  async checkWhatsApp({ commit }, numbers = []) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await BulkCampaignsAPI.checkWhatsApp(numbers);
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isFetching: false,
      });
    }
  },

  async parseCsv({ commit }, { text, roles } = {}) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await BulkCampaignsAPI.parseCsv({
        text,
        roles,
      });
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isFetching: false,
      });
    }
  },

  async testSend({ commit }, { inboxId, content, contact } = {}) {
    commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await BulkCampaignsAPI.testSend({
        inboxId,
        content,
        contact,
      });
      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_CAMPAIGNS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_BULK_CAMPAIGNS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.SET_BULK_CAMPAIGNS]($state, data) {
    $state.records = data;
  },

  [types.APPEND_BULK_CAMPAIGNS]($state, data) {
    const ids = new Set($state.records.map(r => r.id));
    data.forEach(record => {
      if (!ids.has(record.id)) {
        ids.add(record.id);
        $state.records.push(record);
      }
    });
  },

  [types.SET_BULK_CAMPAIGNS_TOTAL]($state, total) {
    $state.totalCount = total;
  },

  [types.SET_BULK_CAMPAIGN_CURRENT]($state, data) {
    $state.currentRecord = data;
  },

  [types.CLEAR_BULK_CAMPAIGN_CURRENT]($state) {
    $state.currentRecord = null;
  },

  [types.ADD_BULK_CAMPAIGN]($state, data) {
    $state.records.unshift(data);
  },

  [types.UPDATE_BULK_CAMPAIGN]($state, data) {
    const index = $state.records.findIndex(record => record.id === data.id);
    if (index > -1) {
      $state.records.splice(index, 1, data);
    }
    if ($state.currentRecord?.id === data.id) {
      $state.currentRecord = data;
    }
  },

  [types.DELETE_BULK_CAMPAIGN]($state, id) {
    $state.records = $state.records.filter(record => record.id !== id);
    if ($state.currentRecord?.id === id) {
      $state.currentRecord = null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
